import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Loader2, Sparkles, Copy, Save, RefreshCcw, Link2,
  Building2, HelpCircle, BookOpen, Briefcase, PenTool, MessageCircle, AtSign, Hash, Check, Code2, Send,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { limitsFor } from "@/lib/plan";
import LockedCard from "@/components/LockedCard";

const CONTENT_TYPE_DEFS = [
  { v: "google_description", icon: Building2 },
  { v: "faq", icon: HelpCircle },
  { v: "about", icon: BookOpen },
  { v: "service_page", icon: Briefcase },
  { v: "blog", icon: PenTool },
  { v: "review_request", icon: MessageCircle },
  { v: "instagram_bio", icon: AtSign },
  { v: "meta_description", icon: Hash },
  { v: "localbusiness_schema", icon: Code2, technical: true },
  { v: "faq_schema", icon: Code2, technical: true },
];

export default function ContentGenerator() {
  const { id } = useParams();
  const { user, refreshUser } = useAuth();
  const { lang, t } = useLang();
  const limits = limitsFor(user);
  const usage = user?.usage || {};
  const monthlyQuota = limits.monthly_content ?? 1;
  const usedThisMonth = usage.monthly_content_count ?? 0;
  const remaining = Math.max(0, monthlyQuota - usedThisMonth);
  const quotaExhausted = remaining === 0 && monthlyQuota < 999;
  const [params] = useSearchParams();
  const initialType = params.get("type") || null;
  const initialTask = params.get("task") || null;

  // Compose localised content type entries (label + description from i18n).
  const CONTENT_TYPES = CONTENT_TYPE_DEFS.map((c) => ({
    ...c,
    t: t(`cg.type.${c.v}`),
    desc: t(`cg.type.${c.v}.desc`),
  }));

  const [type, setType] = useState(initialType);
  const [extra, setExtra] = useState("");
  const [taskId, setTaskId] = useState(initialTask);
  const [output, setOutput] = useState(null);
  const [editing, setEditing] = useState("");
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [linkOpen, setLinkOpen] = useState(false);

  useEffect(() => {
    api.get(`/content/${id}`).then((r) => setHistory(r.data));
    api.get(`/tasks/${id}`).then((r) => setTasks(r.data));
  }, [id]);

  // Auto-generate when arriving from a task with a type pre-selected
  useEffect(() => {
    if (initialTask && initialType && !output && !loading) {
      generate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generate = async () => {
    if (!type) {
      toast.error(t("cg.pickTypeFirst"));
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/content/generate", {
        business_id: id, content_type: type, extra_instructions: extra, task_id: taskId, lang,
      });
      setOutput(data);
      setEditing(data.content);
      refreshUser?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("cg.generateErr"));
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(editing);
    toast.success(t("cg.copiedToast"));
  };

  const save = async () => {
    await api.post("/content/save", { business_id: id, content_type: type, content: editing, task_id: taskId });
    const refreshed = await api.get(`/content/${id}`).then((r) => r.data);
    setHistory(refreshed);
    toast.success(t("cg.savedToast"));
  };

  const bindToTask = (tId) => {
    setTaskId(tId);
    setLinkOpen(false);
    toast.success(t("cg.bindSuccess"));
  };

  const sendToDeveloper = () => {
    const subject = encodeURIComponent(`uSeen - ${output?.title || "Schema code"} - add to website`);
    const body = encodeURIComponent(
      `Hi,\n\nCan you add the following structured data (JSON-LD) snippet to our website?\nGenerated by uSeen.\n\n--- Code start ---\n${editing}\n--- Code end ---\n\nNote: This is LocalBusiness/FAQ Schema and is recommended to be added inside the <head> section.\n`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const linkedTask = tasks.find((t) => t.id === taskId);
  const compatibleTasks = tasks.filter((t) => !type || t.content_type === type || t.content_type === "none");

  return (
    <div className="space-y-6" data-testid="content-generator-page">
      <div>
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-purple-400 mb-2">
          <Sparkles size={12} /> {t("cg.eyebrow")}
        </div>
        <h1 className="font-display text-4xl font-bold text-white">{t("cg.title")}</h1>
        <p className="text-slate-400 mt-2 max-w-3xl">{t("cg.subtitle")}</p>
        {monthlyQuota < 999 && (
          <div className="mt-3 text-xs text-slate-400">
            {t("cg.usage")} <span className="text-white font-mono">{usedThisMonth}</span>
            <span className="text-slate-500"> / </span>
            <span className="text-white font-mono">{monthlyQuota}</span>
            {remaining > 0 && (
              <span className="text-cyan-300"> {t("cg.usage.remaining", { n: remaining })}</span>
            )}
          </div>
        )}
      </div>

      {quotaExhausted && (
        <LockedCard
          title={t("cg.quotaLocked.title")}
          description={t("cg.quotaLocked.desc", { n: monthlyQuota })}
          requiredPlan="Starter / Growth"
          testid="content-locked-card"
        />
      )}

      {/* Step 1: Content type selection */}
      {!output && (
        <>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-3">{t("cg.step1")}</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {CONTENT_TYPES.map((c) => {
                const active = type === c.v;
                return (
                  <button
                    key={c.v}
                    onClick={() => setType(c.v)}
                    className={`text-left rounded-2xl p-4 border transition-all ${
                      active
                        ? "glass-strong border-blue-500/50 shadow-[0_0_30px_rgba(59,130,246,0.2)]"
                        : "glass border-white/10 hover:border-white/25 hover:bg-white/[0.05]"
                    }`}
                    data-testid={`content-type-${c.v}`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 border ${
                      active ? "bg-blue-500/20 border-blue-500/40" : "bg-white/5 border-white/10"
                    }`}>
                      <c.icon size={18} className={active ? "text-cyan-300" : "text-slate-300"} strokeWidth={1.6} />
                    </div>
                    <div className="font-display font-semibold text-white text-sm mb-1">{c.t}</div>
                    <div className="text-xs text-slate-400 leading-relaxed">{c.desc}</div>
                    {active && (
                      <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                        <Check size={12} className="text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="glass-strong rounded-3xl p-6 space-y-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-400 mb-3">{t("cg.step2")}</div>
              <textarea
                value={extra}
                onChange={(e) => setExtra(e.target.value)}
                className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white min-h-[100px] text-sm focus:border-blue-500 focus:outline-none"
                placeholder={t("cg.step2.ph")}
                data-testid="content-extra"
              />
            </div>

            {linkedTask && (
              <div className="rounded-lg bg-cyan-500/10 border border-cyan-500/20 p-3 text-xs text-cyan-100 flex items-center justify-between">
                <span><Link2 size={11} className="inline mr-1" /> {t("cg.linkedTask")} <strong>{linkedTask.title}</strong></span>
                <button onClick={() => setTaskId(null)} className="text-cyan-300 hover:text-cyan-200 text-[11px]">{t("cg.remove")}</button>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <button
                onClick={generate}
                disabled={loading || !type || quotaExhausted}
                className="neon-btn rounded-xl px-6 py-3 text-white font-semibold inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="content-generate"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                {t("cg.generateBtn")}
              </button>
              <button
                onClick={() => setLinkOpen(true)}
                className="ghost-btn rounded-xl px-5 py-3 text-sm text-white inline-flex items-center gap-2"
                data-testid="content-link-task"
              >
                <Link2 size={14} /> {t("cg.linkTaskBtn")}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Step 3: Output */}
      {output && (
        <div className="glass-strong rounded-3xl p-6">
          {(output.content_type === "localbusiness_schema" || output.content_type === "faq_schema") && (
            <div className="rounded-xl bg-cyan-500/5 border border-cyan-500/20 p-3 mb-4 text-xs text-cyan-100 leading-relaxed">
              <Code2 size={12} className="inline mr-1.5 text-cyan-300 -mt-0.5" />
              {t("cg.schemaNote")}
            </div>
          )}
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-wider text-cyan-400 mb-1">{t("cg.outputEyebrow")}</div>
              <h3 className="font-display text-xl font-semibold text-white">{output.title}</h3>
              {linkedTask && <div className="text-xs text-cyan-300 mt-1"><Link2 size={11} className="inline mr-1" /> {linkedTask.title}</div>}
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => { setOutput(null); }} className="ghost-btn rounded-lg px-3 py-2 text-xs text-white" data-testid="content-change-type">
                {t("cg.changeType")}
              </button>
              <button onClick={generate} disabled={loading} className="ghost-btn rounded-lg px-3 py-2 text-xs text-white inline-flex items-center gap-1.5" data-testid="content-regenerate">
                {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCcw size={12} />} {t("common.regenerate")}
              </button>
              <button onClick={copy} className="ghost-btn rounded-lg px-3 py-2 text-xs text-white inline-flex items-center gap-1.5" data-testid="content-copy">
                <Copy size={12} /> {t("common.copy")}
              </button>
              {(output.content_type === "localbusiness_schema" || output.content_type === "faq_schema") && (
                <button
                  onClick={sendToDeveloper}
                  className="ghost-btn rounded-lg px-3 py-2 text-xs text-white inline-flex items-center gap-1.5"
                  data-testid="content-send-developer"
                >
                  <Send size={12} /> {t("cg.sendDev")}
                </button>
              )}
              <button onClick={() => setLinkOpen(true)} className="ghost-btn rounded-lg px-3 py-2 text-xs text-white inline-flex items-center gap-1.5" data-testid="content-link-task-2">
                <Link2 size={12} /> {t("cg.linkTaskBtn2")}
              </button>
              <button onClick={save} className="neon-btn rounded-lg px-3 py-2 text-xs text-white inline-flex items-center gap-1.5 font-medium" data-testid="content-save">
                <Save size={12} /> {t("common.save")}
              </button>
            </div>
          </div>
          <textarea
            value={editing}
            onChange={(e) => setEditing(e.target.value)}
            className="w-full min-h-[340px] bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-slate-100 text-sm leading-relaxed focus:outline-none focus:border-blue-500 font-body"
            data-testid="content-output"
          />
          {output.tips?.length > 0 && (
            <div className="mt-4 rounded-xl bg-cyan-500/5 border border-cyan-500/20 p-4">
              <div className="text-xs uppercase tracking-wider text-cyan-300 mb-2">{t("cg.tips")}</div>
              <ul className="text-sm text-slate-300 space-y-1 list-disc list-inside">
                {output.tips.map((tip, i) => <li key={i}>{tip}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="glass rounded-3xl p-6">
          <div className="text-xs uppercase tracking-wider text-slate-400 mb-3">{t("cg.history")}</div>
          <div className="space-y-2">
            {history.map((h) => {
              const meta = CONTENT_TYPES.find((c) => c.v === h.content_type);
              return (
                <details key={h.id} className="rounded-xl bg-white/[0.02] border border-white/5 p-3" data-testid={`history-${h.id}`}>
                  <summary className="cursor-pointer flex items-center justify-between text-sm">
                    <span className="text-white">{meta?.t || h.content_type}</span>
                    <span className="text-xs text-slate-500 font-mono">{new Date(h.created_at).toLocaleDateString(lang === "en" ? "en-US" : "tr-TR")}</span>
                  </summary>
                  <pre className="mt-3 whitespace-pre-wrap text-sm text-slate-300 font-body">{h.content}</pre>
                </details>
              );
            })}
          </div>
        </div>
      )}

      {/* Bind to task dialog */}
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="glass-strong border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">{t("cg.linkTaskTitle")}</DialogTitle>
            <DialogDescription className="text-slate-400">
              {t("cg.linkTaskDesc")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {compatibleTasks.length === 0 && <div className="text-sm text-slate-500">{t("cg.noLinkableTasks")}</div>}
            {compatibleTasks.map((task) => (
              <button
                key={task.id}
                onClick={() => bindToTask(task.id)}
                className="w-full text-left rounded-lg bg-white/[0.02] border border-white/5 hover:border-blue-500/30 hover:bg-blue-500/5 p-3 transition-all"
              >
                <div className="text-sm text-white">{task.title}</div>
                <div className="text-xs text-slate-500">{task.category} · {task.priority}</div>
              </button>
            ))}
          </div>
          <DialogFooter>
            <button onClick={() => setLinkOpen(false)} className="ghost-btn rounded-lg px-4 py-2 text-sm text-white">{t("common.close")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
